import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useState } from "react";
import { isAndroid, isIOS, isMobile } from "../../../utils/device-detect";
import { useCopyToClipBoard } from "../../hooks/useCopyToClipBoard";
import { useI18n } from "../../hooks/useI18n";
import { useKairosApi } from "../../hooks/useKairosApi";
import { theme } from "../../layout/Theme";
import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary } from "../mui/StyledAccordion";
import { Loading } from "../shared/Loading";
import { PdfView } from "./PdfView";

export function GrantOwnershipForm({ healthRecordId }: { healthRecordId: string | undefined }) {
  const { t } = useI18n();
  const [didFail, setDidFail] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>("");
  const [sms, setSms] = useState<string>("");
  const api = useKairosApi();
  const breakpointSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };
  useEffect(() => {
    const getToken = async () => {
      if (api === undefined || healthRecordId === undefined) {
        return;
      }
      try {
        const { sharingToken } = await api.recordSharingApi.createSharingToken(healthRecordId, {
          grantOwnership: true,
        });
        const shareUrl = new URL(
          `health-records/redeem-sharing-token?token=${sharingToken}`,
          window.location.origin,
        ).toString();
        setShareUrl(shareUrl);
      } catch (_e) {
        setDidFail(true);
      }
    };
    getToken();
  }, [api, healthRecordId]);

  return (
    <Box
      sx={{ mb: 2, backgroundColor: "white", p: { xs: 2, md: 3 }, border: `1px solid ${theme.palette.neutral[300]}` }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, ml: -1 }}>
        <ChevronRightIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
        <Typography variant="h6">{t("share.with_its_owner")}</Typography>
      </Box>
      <Box id="form" sx={{ ml: { xs: 1, md: 3 } }}>
        {isMobile && (isIOS || isAndroid) && renderMobileShare()}
        <EmailSharing shareUrl={shareUrl} />
        <Divider sx={{ my: 2, color: theme.palette.primary.main }}>{t("share.or")}</Divider>
        <StyledAccordion expanded={expanded === "panel1"} onChange={handleChange("panel1")} elevation={0}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="body2">{t("share.other_transfer_methods")}</Typography>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <QrCodeSharing shareUrl={shareUrl} />

            <Divider sx={{ my: 2, color: theme.palette.primary.main }}>{t("share.or")}</Divider>
            <LinkSharing shareUrl={shareUrl} />
            <Box display="none">
              <Typography>{"It's Hidden"}</Typography>
              {shareUrl ? <QRCodeSVG value={shareUrl} size={200} /> : <Loading />}
            </Box>
            <PdfView id="Pdf" healthRecordId={healthRecordId as string} shareUrl={shareUrl ?? ""} />
          </StyledAccordionDetails>
        </StyledAccordion>
      </Box>
      {didFail && (
        <Alert data-testid="alert" severity="error" sx={{ marginY: 1 }}>
          {t("common.alerts.alert_notification")}
        </Alert>
      )}
    </Box>
  );

  function renderMobileShare() {
    const link = `sms://${sms}${
      isAndroid ? "?" : "&"
    }body=Pour récupérer votre carnet cliquez sur le lien suivant : ${shareUrl}`;
    return (
      <>
        <Typography>{t("share.sms_grant_ownership")}</Typography>
        <TextField
          placeholder="sms"
          onChange={(e) => setSms(e.target.value)}
          variant="outlined"
          InputProps={{
            "aria-label": "numéro de téléphone du destinataire",
            startAdornment: (
              <InputAdornment position="start">
                <SmsIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                <IconButton color="primary" sx={{ p: "10px" }} aria-label="directions" href={link}>
                  <SendIcon />
                </IconButton>
              </>
            ),
          }}
          sx={{ my: 2, width: breakpointSmDown ? "100%" : "50%" }}
        />
        <Divider sx={{ my: 2, color: theme.palette.primary.main }}>{t("share.or")}</Divider>
      </>
    );
  }
}

const EmailSharing = ({ shareUrl }: { shareUrl: string }) => {
  const [email, setEmail] = useState<string>("");
  const [validEmail, setValidEmail] = useState<boolean | undefined>(undefined);
  const { t } = useI18n();

  return (
    <Stack>
      <Typography variant="body2">{t("share.email_grant_ownership")}</Typography>
      <TextField
        placeholder="@"
        value={email}
        onChange={(e) => {
          setValidEmail(/^[a-z0-9._\-+]+@[a-z0-9._-]{2,}\.[a-z]{2,4}$/i.test(e.target.value));
          setEmail(e.target.value);
        }}
        type={"email"}
        variant="outlined"
        color={email === "" ? "primary" : validEmail ? "success" : "error"}
        InputProps={{
          "aria-label": "Email de partage",
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color={email !== "" ? (validEmail ? "success" : "error") : "inherit"} />
            </InputAdornment>
          ),
          endAdornment: (
            <>
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <a
                href={`mailto:${email}?subject=Votre carnet de vaccination&body=Pour récupérer votre carnet cliquez sur le lien suivant : ${shareUrl}`}
              >
                <IconButton
                  color={email !== "" ? (validEmail ? "success" : "error") : "primary"}
                  sx={{ p: 1 }}
                  aria-label="directions"
                >
                  <SendIcon />
                </IconButton>
              </a>
            </>
          ),
        }}
        size="small"
        fullWidth
        sx={{ my: 1, width: { xs: "100%", md: "50%" } }}
      />
      <Typography variant="body2" color={theme.palette.neutral[500]}>
        {t("share.email_sharing_description")}
      </Typography>
    </Stack>
  );
};

const QrCodeSharing = ({ shareUrl }: { shareUrl: string | null }) => {
  const { t } = useI18n();

  const generatePdf = useCallback(() => {
    const qrCode = document.getElementById("Pdf");
    if (qrCode) {
      const pdfWindow = window.open("pdf", "PRINT");
      if (!pdfWindow) return;
      pdfWindow.document.write(qrCode.innerHTML);
      pdfWindow.document.close();
      pdfWindow.focus();
      pdfWindow.print();
      pdfWindow.close();
    }
  }, []);
  return (
    <>
      <Typography variant="body2">{t("share.qrcode_grant_ownership")}</Typography>

      <Typography variant="body2" color={theme.palette.neutral[500]}>
        {t("share.qrcode_share_description")}
      </Typography>
      <Box id="QrCode" sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "center", p: 2 }}>
        {shareUrl ? (
          <Stack spacing={2}>
            <QRCodeSVG value={shareUrl} size={150} />{" "}
            <Button variant="outlined" onClick={() => generatePdf()}>
              {t("share.generate_pdf")}
            </Button>
          </Stack>
        ) : (
          <Loading />
        )}
      </Box>
    </>
  );
};

const LinkSharing = ({ shareUrl }: { shareUrl: string | null }) => {
  const { t } = useI18n();
  const [copied, copyToClipBoard] = useCopyToClipBoard(shareUrl ?? "");

  return (
    <>
      <Typography variant="body2">{t("share.grant_ownership_link")}</Typography>
      <Typography variant="body2" color={theme.palette.neutral[500]}>
        {t("share.share_link_description")}
      </Typography>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        value={copied ? t("common.copied") : (shareUrl ?? "")}
        color={copied ? "success" : "primary"}
        InputProps={{
          readOnly: true,
          "aria-label": "Lien de partage",
          endAdornment: (
            <>
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <IconButton
                color={copied ? "success" : "primary"}
                sx={{ p: "10px" }}
                aria-label="directions"
                onClick={copyToClipBoard}
              >
                <ContentCopyIcon />
              </IconButton>
            </>
          ),
        }}
        sx={{ my: 2 }}
      />
    </>
  );
};
